/**
 * JiraPreview
 * Preview and edit JIRA epics and stories before pushing to JIRA
 */

import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Plus, Upload } from 'lucide-react';
import { ProposedEpic, ProposedStory } from '../../../types';
import { StoryEditor } from '../StoryEditor';
import { Button } from '../../ui/Button';
import { Input } from '../../ui/Input';
import { Textarea } from '../../ui/Textarea';
import styles from './JiraPreview.module.css';

export interface JiraPreviewProps {
  epics: ProposedEpic[];
  onUpdateEpics: (epics: ProposedEpic[]) => void;
  onPushToJira: () => void;
  isPushing?: boolean;
}

export const JiraPreview: React.FC<JiraPreviewProps> = ({
  epics,
  onUpdateEpics,
  onPushToJira,
  isPushing = false,
}) => {
  const [expandedEpics, setExpandedEpics] = useState<Set<string>>(
    new Set(epics.map((e) => e.id))
  );
  const [editingEpicId, setEditingEpicId] = useState<string | null>(null);

  const toggleEpic = (epicId: string) => {
    const newExpanded = new Set(expandedEpics);
    if (newExpanded.has(epicId)) {
      newExpanded.delete(epicId);
    } else {
      newExpanded.add(epicId);
    }
    setExpandedEpics(newExpanded);
  };

  const handleUpdateStory = (epicId: string, storyId: string, updatedStory: ProposedStory) => {
    const updatedEpics = epics.map((epic) => {
      if (epic.id === epicId) {
        return {
          ...epic,
          stories: epic.stories.map((story) =>
            story.id === storyId ? updatedStory : story
          ),
        };
      }
      return epic;
    });
    onUpdateEpics(updatedEpics);
  };

  const handleDeleteStory = (epicId: string, storyId: string) => {
    const updatedEpics = epics.map((epic) => {
      if (epic.id === epicId) {
        return {
          ...epic,
          stories: epic.stories.filter((story) => story.id !== storyId),
        };
      }
      return epic;
    });
    onUpdateEpics(updatedEpics);
  };

  const handleAddStory = (epicId: string) => {
    const newStory: ProposedStory = {
      id: `story-${Date.now()}`,
      title: 'New Story',
      description: '',
      acceptanceCriteria: [],
      priority: 'Medium',
    };
    const updatedEpics = epics.map((epic) => {
      if (epic.id === epicId) {
        return {
          ...epic,
          stories: [...epic.stories, newStory],
        };
      }
      return epic;
    });
    onUpdateEpics(updatedEpics);
  };

  const handleAddEpic = () => {
    const newEpic: ProposedEpic = {
      id: `epic-${Date.now()}`,
      title: 'New Epic',
      description: '',
      stories: [],
    };
    onUpdateEpics([...epics, newEpic]);
    setExpandedEpics(new Set([...expandedEpics, newEpic.id]));
  };

  const handleDeleteEpic = (epicId: string) => {
    const updatedEpics = epics.filter((epic) => epic.id !== epicId);
    onUpdateEpics(updatedEpics);
  };

  const totalStories = epics.reduce((sum, epic) => sum + epic.stories.length, 0);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <h3 className={styles.title}>JIRA Items Preview</h3>
          <div className={styles.counts}>
            <span>{epics.length} Epics</span>
            <span>•</span>
            <span>{totalStories} Stories</span>
          </div>
        </div>
        <div className={styles.actions}>
          <Button variant="ghost" size="sm" onClick={handleAddEpic}>
            <Plus size={16} />
            Add Epic
          </Button>
          <Button
            variant="primary"
            onClick={onPushToJira}
            disabled={isPushing || epics.length === 0}
          >
            <Upload size={16} />
            {isPushing ? 'Pushing...' : 'Push to JIRA'}
          </Button>
        </div>
      </div>

      {epics.length === 0 ? (
        <div className={styles.empty}>
          <p>No JIRA items to preview.</p>
          <Button variant="primary" size="sm" onClick={handleAddEpic}>
            <Plus size={16} />
            Add Epic
          </Button>
        </div>
      ) : (
        <div className={styles.epicsList}>
          {epics.map((epic) => {
            const isExpanded = expandedEpics.has(epic.id);
            const isEditing = editingEpicId === epic.id;

            return (
              <div key={epic.id} className={styles.epicCard}>
                <div className={styles.epicHeader}>
                  <button
                    className={styles.expandButton}
                    onClick={() => toggleEpic(epic.id)}
                    aria-label={isExpanded ? 'Collapse' : 'Expand'}
                  >
                    {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                  </button>
                  <div className={styles.epicInfo}>
                    <h4 className={styles.epicTitle}>{epic.title}</h4>
                    <p className={styles.epicDescription}>{epic.description}</p>
                    <div className={styles.epicMeta}>
                      <span>{epic.stories.length} stories</span>
                    </div>
                  </div>
                  <div className={styles.epicActions}>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setEditingEpicId(isEditing ? null : epic.id)}
                    >
                      {isEditing ? 'Done' : 'Edit'}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteEpic(epic.id)}
                    >
                      Delete
                    </Button>
                  </div>
                </div>

                {isExpanded && (
                  <div className={styles.storiesContainer}>
                    {isEditing && (
                      <div className={styles.epicEditor}>
                        <div className={styles.field}>
                          <label className={styles.label}>Epic Title</label>
                          <Input
                            value={epic.title}
                            onChange={(e) => {
                              const updatedEpics = epics.map((ep) =>
                                ep.id === epic.id ? { ...ep, title: e.target.value } : ep
                              );
                              onUpdateEpics(updatedEpics);
                            }}
                          />
                        </div>
                        <div className={styles.field}>
                          <label className={styles.label}>Epic Description</label>
                          <Textarea
                            value={epic.description}
                            onChange={(e) => {
                              const updatedEpics = epics.map((ep) =>
                                ep.id === epic.id ? { ...ep, description: e.target.value } : ep
                              );
                              onUpdateEpics(updatedEpics);
                            }}
                            rows={3}
                          />
                        </div>
                      </div>
                    )}

                    <div className={styles.storiesList}>
                      {epic.stories.map((story) => (
                        <StoryEditor
                          key={story.id}
                          story={story}
                          onUpdate={(updatedStory) =>
                            handleUpdateStory(epic.id, story.id, updatedStory)
                          }
                          onDelete={() => handleDeleteStory(epic.id, story.id)}
                        />
                      ))}
                    </div>

                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleAddStory(epic.id)}
                      className={styles.addStoryButton}
                    >
                      <Plus size={16} />
                      Add Story
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default JiraPreview;
